/**
 * Free Gift Button - Consolidated functionality for free gift promotions
 * Combines cart utilities and React component integration
 */

class FreeGiftPromoIntegration {
    static instance = null;
    static freeGiftVariantIds = new Set();
    static isInitialized = false;
    static reactRoot = null;

    constructor() {
        // Prevent multiple instances
        if (FreeGiftPromoIntegration.instance) {
            console.log('FreeGiftPromoIntegration instance already exists, returning existing instance');
            return FreeGiftPromoIntegration.instance;
        }

        this.isInitialized = false;
        this.reactRoot = null;
        FreeGiftPromoIntegration.instance = this;
        this.init();
    }

    // CART UTILITIES

    /**
     * Check if a cart item is a free gift based on its variant ID
     * @param {string} variantId - The variant ID to check
     * @returns {boolean} - True if the item is a free gift
     */
    static isFreeGiftItem(variantId) {
        return FreeGiftPromoIntegration.freeGiftVariantIds.has(variantId.toString());
    }

    /**
     * Update the list of free gift variant IDs
     * This should be called when free gifts are loaded
     * @param {Array} freeGifts - Array of free gift objects with id property
     */
    static updateFreeGiftVariantIds(freeGifts) {
        FreeGiftPromoIntegration.freeGiftVariantIds.clear();
        freeGifts.forEach(gift => {
            // Convert Shopify GID to numeric ID if needed
            const giftNumericId = gift.id.startsWith('gid://shopify/ProductVariant/')
                ? gift.id.split('/').pop()
                : gift.id;
            FreeGiftPromoIntegration.freeGiftVariantIds.add(giftNumericId);
        });
    }

    /**
     * Disable quantity controls for free gift items
     */
    static disableFreeGiftQuantityControls() {
        // Find all cart item quantity controls
        const quantityControls = document.querySelectorAll('.cart-item-quantity-controls, .cart-drawer-item-quantity-controls, [class*="quantity-controls"]');

        quantityControls.forEach(control => {
            // Find the parent cart item element (check both data-cart-item and data-item-key)
            const cartItem = control.closest('[data-cart-item]') || control.closest('[data-item-key]');

            // Check if this specific cart item is a free gift (not just the variant ID)
            // This ensures that if the same product exists twice (one free gift, one regular),
            // only the free gift version is disabled
            const isFreeGift = cartItem?.dataset.freeGift === 'true';

            if (isFreeGift) {
                // Disable functionality only - NO visual changes
                const input = control.querySelector('input[type="number"]');
                const buttons = control.querySelectorAll('button, a');

                if (input) {
                    input.readOnly = true;
                }

                buttons.forEach(button => {
                    button.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    });
                });
            }
        });
    }

    // Debounce utility to prevent excessive calls
    static disableTimeout = null;
    static debouncedDisableControls() {
        if (FreeGiftPromoIntegration.disableTimeout) {
            clearTimeout(FreeGiftPromoIntegration.disableTimeout);
        }
        FreeGiftPromoIntegration.disableTimeout = setTimeout(FreeGiftPromoIntegration.disableFreeGiftQuantityControls, 150);
    }

    /**
     * Initialize free gift cart utilities
     */
    static initCartUtils() {
        // Remove existing event listeners to prevent duplicates
        const existingListeners = window.freeGiftCartListeners || [];
        existingListeners.forEach(({ event, handler }) => {
            document.removeEventListener(event, handler);
        });

        // Create single event handler
        const cartEventHandler = () => FreeGiftPromoIntegration.debouncedDisableControls();

        // Store listeners for cleanup
        const listeners = [
            { event: 'cart:update', handler: cartEventHandler },
            { event: 'cart:add', handler: cartEventHandler },
            { event: 'cart:change', handler: cartEventHandler }
        ];

        // Add event listeners
        listeners.forEach(({ event, handler }) => {
            document.addEventListener(event, handler);
        });

        // Store for potential cleanup
        window.freeGiftCartListeners = listeners;

        // Watch for DOM changes to catch dynamically added cart items
        const observer = new MutationObserver((mutations) => {
            const hasCartItems = mutations.some((mutation) => {
                if (mutation.type !== 'childList') return false;

                return Array.from(mutation.addedNodes).some((node) => {
                    if (node.nodeType !== Node.ELEMENT_NODE) return false;

                    const element = node;
                    return element.classList?.contains('cart-item') ||
                        element.classList?.contains('cart-drawer-item') ||
                        element.querySelector?.('.cart-item, .cart-drawer-item');
                });
            });

            if (hasCartItems) {
                FreeGiftPromoIntegration.debouncedDisableControls();
            }
        });

        // Start observing
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Single initial disable attempt
        FreeGiftPromoIntegration.debouncedDisableControls();
    }

    // REACT INTEGRATION

    async init() {
        if (this.isInitialized) {
            console.log('FreeGiftPromoIntegration already initialized, skipping init()');
            return;
        }

        // Initialize cart utilities first
        FreeGiftPromoIntegration.initCartUtils();

        if (this.isProductPage()) {
            this.initializeFreeGiftPromo();
            this._listenForVariantChanges();
        }
        else if (this.isCartPage()) {
            this.initializeCartFreeGiftPromo();
        }
        else {
            return;
        }

        this.isInitialized = true;
    }

    isProductPage() {
        // Collection pages have product forms in their cards, so exclude them first
        // to avoid false positives from the form/input checks below.
        if (window.location.pathname.includes('/collections/')) {
            return false;
        }

        // Method 1: Check for template-product class
        if (document.body.classList.contains('template-product')) {
            return true;
        }

        // Method 2: Check URL pathname for /products/
        if (window.location.pathname.includes('/products/')) {
            return true;
        }

        // Method 3: Check if we have a product form (add to cart form)
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
            return true;
        }

        // Method 4: Check if we have variant selectors
        const variantSelectors = document.querySelectorAll('input[name="id"]');
        if (variantSelectors.length > 0) {
            return true;
        }

        // Method 5: Check if we have our free gift container (this means we're on the right page)
        const freeGiftContainer = document.getElementById('free-gift-promo-container');
        if (freeGiftContainer) {
            return true;
        }

        return false;
    }

    isCartPage() {
        // Method 1: Check for template-cart class
        if (document.body.classList.contains('template-cart')) {
            return true;
        }

        // Method 2: Check URL pathname for /cart
        if (window.location.pathname.includes('/cart')) {
            return true;
        }

        // Method 3: Check if we have cart items container
        const cartItemsContainer = document.querySelector('[data-cart-items-container]');
        if (cartItemsContainer) {
            return true;
        }

        // Method 4: Check if we have our cart free gift container
        const cartFreeGiftContainer = document.getElementById('cart-free-gift-promo-container');
        if (cartFreeGiftContainer) {
            return true;
        }

        return false;
    }

    async initializeFreeGiftPromo() {
        if (this.reactRoot) {
            console.log('FreeGiftPromo already initialized, skipping...');
            return;
        }

        try {
            // Load the React app if not already loaded
            if (!window.FreeGiftPromo) {
                await this.loadReactApp();
            }

            // Create container for the free gift promo
            const container = this.createPromoContainer();
            if (!container) {
                console.warn('FreeGiftPromo: Container not found, skipping initialization');
                return;
            }

            // Initialize the React component
            this.renderFreeGiftPromo(container);

        } catch (error) {
            console.error('Failed to initialize Free Gift Promo:', error);
        }
    }

    async loadReactApp() {
        // If already loaded, return immediately
        if (window.FreeGiftPromo) {
            return Promise.resolve();
        }

        // Check if the script is included in the HTML
        const scriptElement = document.querySelector('script[src*="product-search.js"]');
        if (!scriptElement) {
            throw new Error('FreeGiftPromo script not found.');
        }

        // Wait for the React app to be available (script should be loaded via HTML)
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('FreeGiftPromo failed to load.'));
            }, 10000);

            const checkApp = () => {
                if (window.FreeGiftPromo) {
                    clearTimeout(timeout);
                    resolve();
                }
            };

            // Check immediately in case it's already loaded
            checkApp();

            // Listen for the app to be ready
            document.addEventListener('freeGiftPromoReady', checkApp, { once: true });
        });
    }

    createPromoContainer() {
        // Find the existing container
        const container = document.getElementById('free-gift-promo-container');
        return container;
    }

    createCartPromoContainer() {
        // Find the cart free gift container
        const container = document.getElementById('cart-free-gift-promo-container');
        return container;
    }

    async initializeCartFreeGiftPromo() {
        if (this.reactRoot) {
            console.log('Cart FreeGiftPromo already initialized, skipping...');
            return;
        }

        try {
            // Load the React app if not already loaded
            if (!window.FreeGiftPromo) {
                await this.loadReactApp();
            }

            // Create container for the cart free gift promo
            const container = this.createCartPromoContainer();
            if (!container) {
                console.warn('Cart FreeGiftPromo: Container not found, skipping initialization');
                return;
            }

            // Initialize the React component for cart
            await this.renderCartFreeGiftPromo(container);

            // On collection pages, FreeGiftPromo loads before cart items exist,
            // so no promo rules are applied. Re-initialise on each cart:update
            // to use fresh cart data and trigger the modal correctly.
            if (window.location.pathname.includes('/collections/')) {
                this._listenForCartUpdatesOnCollection();
            }

        } catch (error) {
            console.error('Failed to initialize Cart Free Gift Promo:', error);
        }
    }

    _listenForCartUpdatesOnCollection() {
        if (this._collectionCartListener) return; // bind once only

        let debounceTimer = null;

        this._collectionCartListener = () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(async () => {
                // Don't re-init while the gift selection modal is open
                if (document.querySelector('.free-gift-modal-overlay')) return;

                const container = this.createCartPromoContainer();
                if (!container) return;

                await this.renderCartFreeGiftPromo(container);
            }, 1500);
        };

        document.addEventListener('cart:update', this._collectionCartListener);
    }

    initFreeGiftPromoCommon(container, items, variantId = null) {
        importModule('Utils').then(modules => {
            const { publishEvent } = modules;
            this.reactRoot = window.FreeGiftPromo.init(container, {
                cartItems: items,
                variantId: variantId,
                onGiftUpdate: (giftVariantId, newQuantity) => {
                    console.log('Free gift updated in cart:', giftVariantId, newQuantity);
                },
                publishEvent
            });
        })
    }

    async renderFreeGiftPromo(container) {
        if (!container || !window.FreeGiftPromo) {
            return;
        }

        // Get current variant ID
        const variantId = this.getCurrentVariantId();

        // Initialize the React app with the variant ID
        this.initFreeGiftPromoCommon(container, [], variantId);
        this._observePickability(container);
    }

    /**
     * Listen for variant:change events on the document.
     * If the free gift React root hasn't been initialized yet, attempt to initialize it.
     * If it's already initialized, destroy and re-render with the new variant.
     */
    _listenForVariantChanges() {
        document.addEventListener('variant:change', (event) => {
            const data = event.detail?.data;
            if (!data) return;

            const newVariantId = data.variantId?.toString() || null;

            // If free gift hasn't been rendered yet, try to initialize it now
            if (!this.reactRoot) {
                this.initializeFreeGiftPromo();
                return;
            }

            // Already initialized — destroy and re-render with the new variant
            if (this.reactRoot?.destroy) {
                this.reactRoot.destroy();
                this.reactRoot = null;
            }

            // Wait for product-variant-listener to re-render the DOM before we re-query
            requestAnimationFrame(() => {
                const freshContainer = document.getElementById('free-gift-promo-container');
                if (!freshContainer) return;

                this.initFreeGiftPromoCommon(freshContainer, [], newVariantId);
                this._observePickability(freshContainer);
            });
        });
    }

    /**
     * Observe the free gift container to determine if a gift is pickable.
     * The React app renders a .free-gift-select-button when pickable, or nothing when not.
     * Sets a data-gift-pickable attribute on the wrapper for external consumers.
     */
    _observePickability(container) {
        if (this._pickabilityObserver) {
            this._pickabilityObserver.disconnect();
        }

        const checkPickability = () => {
            const isPickable = container.querySelector('.free-gift-select-button') !== null;
            const wrapper = container.closest('.free-gift-promo-wrapper') || container;
            wrapper.toggleAttribute('data-gift-pickable', isPickable);
        };

        checkPickability();

        this._pickabilityObserver = new MutationObserver(checkPickability);
        this._pickabilityObserver.observe(container, {
            childList: true,
            subtree: true
        });
    }

    async renderCartFreeGiftPromo(container) {
        if (!container || !window.FreeGiftPromo) {
            return;
        }

        // Get cart items
        const cartItems = this.getCartItems();

        // If no cart items found from DOM, try to fetch from API
        if (cartItems.length === 0) {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();

                if (cart.items && cart.items.length > 0) {
                    const apiCartItems = cart.items.map(item => item.variant_id.toString());
                    this.initFreeGiftPromoCommon(container, apiCartItems);
                } else {
                    this.initFreeGiftPromoCommon(container, []);
                }
            } catch (error) {
                console.error('Error fetching cart from API:', error);
                // Initialize with empty array as fallback
                this.initFreeGiftPromoCommon(container, []);
            }
        } else {
            this.initFreeGiftPromoCommon(container, cartItems);
        }
    }

    getCurrentVariantId() {
        // Try to get variant ID from URL
        const urlParams = new URLSearchParams(window.location.search);
        const variant = urlParams.get('variant');
        if (variant) {
            return variant;
        }

        // Try to get from product form
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
            const idInput = productForm.querySelector('input[name="id"]');
            if (idInput) {
                return idInput.value;
            }
        }

        // Try to get from variant selector
        const variantInput = document.querySelector('input[name="id"]:checked');
        if (variantInput) {
            return variantInput.value;
        }

        return null;
    }

    getCartItems() {
        // Extract cart items from the cart page
        const cartItems = [];

        // Look for cart item elements
        const cartItemElements = document.querySelectorAll('[data-cart-item]');

        cartItemElements.forEach((item) => {
            // Try to get variant ID from various possible attributes
            const variantId = item.getAttribute('data-variant-id') ||
                item.querySelector('[name="id"]')?.value ||
                item.querySelector('input[name="id"]')?.value;

            if (variantId) {
                cartItems.push(variantId);
            }
        });

        // Try alternative selectors if no items found
        if (cartItems.length === 0) {
            // Try to find variant IDs in cart-qty-button elements
            const cartQtyButtons = document.querySelectorAll('cart-qty-button[variant-id]');

            cartQtyButtons.forEach((button) => {
                const variantId = button.getAttribute('variant-id');
                if (variantId && !cartItems.includes(variantId)) {
                    cartItems.push(variantId);
                }
            });

            // Try to find variant IDs in cart-qty-input elements
            const cartQtyInputs = document.querySelectorAll('cart-qty-input[variant-id]');

            cartQtyInputs.forEach((input) => {
                const variantId = input.getAttribute('variant-id');
                if (variantId && !cartItems.includes(variantId)) {
                    cartItems.push(variantId);
                }
            });

            // Try to find variant IDs in form inputs
            const variantInputs = document.querySelectorAll('input[name="id"]');

            variantInputs.forEach((input) => {
                if (input.value && !cartItems.includes(input.value)) {
                    cartItems.push(input.value);
                }
            });
        }

        return cartItems;
    }
}

// GLOBAL EXPORTS

// Export cart utility functions to global scope for backward compatibility
window.isFreeGiftItem = FreeGiftPromoIntegration.isFreeGiftItem.bind(FreeGiftPromoIntegration);
window.updateFreeGiftVariantIds = FreeGiftPromoIntegration.updateFreeGiftVariantIds.bind(FreeGiftPromoIntegration);
window.disableFreeGiftQuantityControls = FreeGiftPromoIntegration.disableFreeGiftQuantityControls.bind(FreeGiftPromoIntegration);
window.initFreeGiftCartUtils = FreeGiftPromoIntegration.initCartUtils.bind(FreeGiftPromoIntegration);

// Singleton pattern to prevent multiple initializations
let isInitialized = false;
let initializationPromise = null;

// Initialize when DOM is ready
const initializeWhenReady = () => {
    if (isInitialized) {
        console.log('FreeGiftPromoIntegration already initialized, skipping...');
        return;
    }

    if (initializationPromise) {
        console.log('FreeGiftPromoIntegration initialization already in progress, skipping...');
        return;
    }

    console.log('Initializing FreeGiftPromoIntegration...');
    isInitialized = true;
    initializationPromise = new Promise((resolve) => {
        new FreeGiftPromoIntegration();
        resolve();
    });
};

// Try multiple approaches to ensure we initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWhenReady);

    // Fallback: if DOMContentLoaded doesn't fire within 3 seconds, initialize anyway
    setTimeout(() => {
        initializeWhenReady();
    }, 3000);
} else {
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(initializeWhenReady, 100);
}
